/**
 * SEO Helper - Utilitários para meta tags dinâmicas e SEO
 * Otimizado para buscadores (Google, Bing) e motores de IA (ChatGPT, Perplexity, Claude)
 */

import React from 'react'

// Meta tags padrão do site
const DEFAULT_META = {
  title: 'Temporiza - Assinatura Eletrônica ICP-Brasil | Validade Jurídica',
  description: 'Plataforma brasileira de assinatura eletrônica com validade jurídica e selo ITI. Autenticação via email, SMS ou WhatsApp. Suporte a certificado digital A1 ICP-Brasil, workflows inteligentes e extração IA de documentos.',
  keywords: 'assinatura eletrônica, assinatura digital, ICP-Brasil, validade jurídica, LGPD, assinatura online, documento digital, autenticação SMS, workflow aprovação, API assinatura eletrônica, selo ITI, certificado A1, assinatura qualificada',
  author: 'Temporiza',
  viewport: 'width=device-width, initial-scale=1.0',
  robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  language: 'pt-BR'
}

// URLs e informações do site
const SITE_CONFIG = {
  name: 'Temporiza',
  tagline: 'Assinatura Eletrônica ICP-Brasil',
  url: import.meta.env.VITE_SITE_URL || 'https://temporiza.com',
  logo: '/images/og-image.jpg',
  twitter: '@temporiza',
  facebook: 'temporiza',
  linkedin: 'company/temporiza',
  email: 'contact@temporiza.com',
  location: 'São Paulo, Brasil'
}

/**
 * Gera meta tags dinâmicas para uma página
 * @param {Object} pageConfig - Configuração específica da página
 * @returns {Object} Meta tags completas
 */
export const generateMetaTags = (pageConfig = {}) => {
  const config = { ...DEFAULT_META, ...pageConfig }

  return {
    // Meta tags básicas
    title: config.title,
    description: config.description,
    keywords: config.keywords,
    author: config.author,
    viewport: config.viewport,
    robots: config.robots,
    language: config.language,

    // Open Graph
    'og:type': config.type || 'website',
    'og:title': config.title,
    'og:description': config.description,
    'og:url': config.url || SITE_CONFIG.url,
    'og:site_name': SITE_CONFIG.name,
    'og:image': config.image || `${SITE_CONFIG.url}${SITE_CONFIG.logo}`,
    'og:image:width': config.imageWidth || '1200',
    'og:image:height': config.imageHeight || '630',
    'og:locale': 'pt_BR',
    'og:locale:alternate': ['en_US', 'es_ES'],

    // Twitter Cards
    'twitter:card': config.twitterCard || 'summary_large_image',
    'twitter:site': SITE_CONFIG.twitter,
    'twitter:creator': SITE_CONFIG.twitter,
    'twitter:title': config.title,
    'twitter:description': config.description,
    'twitter:image': config.image || `${SITE_CONFIG.url}${SITE_CONFIG.logo}`,

    // Meta tags para LLMs e AI assistants (ChatGPT, Perplexity, Claude)
    ...(config.aiSummary && { 'ai:summary': config.aiSummary }),

    // Structured Data
    'application/ld+json': generateStructuredData(config)
  }
}

/**
 * Gera dados estruturados (Schema.org) para SEO
 * @param {Object} config - Configuração da página
 * @returns {string} JSON-LD estruturado
 */
export const generateStructuredData = (config) => {
  // Schema da organização - OTIMIZADO PARA LLMs
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_CONFIG.url}/#organization`,
    name: 'Temporiza',
    legalName: 'Temporiza Tecnologia LTDA',
    url: SITE_CONFIG.url,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_CONFIG.url}${SITE_CONFIG.logo}`
    },
    description: 'Startup brasileira de tecnologia especializada em assinatura eletrônica compatível com ICP-Brasil. Fundada em 2024 em São Paulo, oferece plataforma de assinatura digital com validade jurídica, autenticação multi-método e extração de dados via IA.',
    email: SITE_CONFIG.email,
    foundingDate: '2024',
    foundingLocation: {
      '@type': 'Place',
      name: 'São Paulo, Brasil'
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'AV BRG FARIA LIMA 1811 SALA ESC 1119 - Jd Paulistano',
      addressLocality: 'São Paulo',
      addressRegion: 'SP',
      postalCode: '01452-001',
      addressCountry: 'BR'
    },
    areaServed: {
      '@type': 'Country',
      name: 'Brasil'
    },
    knowsAbout: [
      'Assinatura eletrônica',
      'ICP-Brasil',
      'Certificado digital A1',
      'Selo ITI',
      'Assinatura qualificada',
      'LGPD',
      'Extração de dados com IA',
      'Workflows de aprovação'
    ],
    sameAs: [
      `https://twitter.com/${SITE_CONFIG.twitter.replace('@', '')}`,
      `https://linkedin.com/${SITE_CONFIG.linkedin}`,
      'https://temporiza.readme.io/',
      'https://app.temporiza.com/'
    ]
  }

  // Schema do WebSite com busca
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_CONFIG.url}/#website`,
    name: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    description: config.description,
    publisher: { '@id': `${SITE_CONFIG.url}/#organization` },
    inLanguage: ['pt-BR', 'en-US', 'es-ES']
  }

  // Schema do Software/Produto - OTIMIZADO PARA LLMs
  const softwareSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${SITE_CONFIG.url}/#software`,
    name: 'Temporiza',
    alternateName: 'Temporiza Assinatura Eletrônica',
    description: 'Plataforma brasileira de assinatura eletrônica com suporte a ICP-Brasil para validade jurídica. Oferece autenticação por email, SMS e WhatsApp, workflows de aprovação configuráveis, extração de dados via IA usando LLMs, API REST com webhooks, e editor visual de templates PDF. Plano gratuito permanente disponível.',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Assinatura Eletrônica',
    operatingSystem: 'Web, iOS, Android',
    softwareVersion: '2.0',
    datePublished: '2024-01-01',
    inLanguage: ['pt-BR', 'en-US', 'es-ES'],
    countriesSupported: 'Brasil',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '0',
      highPrice: '99.90',
      priceCurrency: 'BRL',
      offerCount: 3,
      availability: 'https://schema.org/InStock'
    },
    featureList: [
      'Assinatura eletrônica com validade jurídica ICP-Brasil',
      'Selo ITI de conformidade - validação oficial pelo Instituto Nacional de Tecnologia da Informação',
      'Suporte a certificado digital A1 com armazenamento seguro criptografado',
      'Assinatura qualificada com validade jurídica ICP-Brasil',
      'Verificação pública de documentos no portal validar.iti.gov.br',
      'Suporte a ICP-Brasil (MP 2.200-2/2001)',
      'Autenticação por email (link mágico)',
      'Autenticação por SMS (código PIN)',
      'Autenticação por WhatsApp',
      'Workflows de aprovação sequenciais e paralelos',
      'Extração IA de dados de documentos PDF usando LLMs',
      'Editor visual drag-and-drop de templates PDF',
      'Detecção automática de campos AcroForm',
      'Formulários de intake público com 2FA',
      'Múltiplos signatários com ordenação',
      'API REST documentada com webhooks em tempo real',
      'Conformidade LGPD (Lei Geral de Proteção de Dados)',
      'Gestão de documentos em pastas',
      'Playbooks para validação automática',
      'Histórico e trilha de auditoria completa'
    ],
    keywords: 'assinatura eletrônica, ICP-Brasil, assinatura digital, certificado digital A1, selo ITI, assinatura qualificada, LGPD, validade jurídica, MP 2200-2, assinatura online, workflow aprovação, extração IA PDF',
    author: { '@id': `${SITE_CONFIG.url}/#organization` },
    provider: { '@id': `${SITE_CONFIG.url}/#organization` },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '127',
      bestRating: '5'
    }
  }

  // Esquemas específicos por tipo de página
  let pageSchema = null

  switch (config.schemaType) {
    case 'FAQPage':
      pageSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: config.faqItems || []
      }
      break

    case 'Product':
      pageSchema = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: `${SITE_CONFIG.name} - ${config.title}`,
        description: config.description,
        brand: { '@id': `${SITE_CONFIG.url}/#organization` },
        offers: config.offers || softwareSchema.offers
      }
      break

    case 'Service':
      pageSchema = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: config.title,
        description: config.description,
        provider: { '@id': `${SITE_CONFIG.url}/#organization` },
        serviceType: 'Assinatura Eletrônica',
        areaServed: {
          '@type': 'Country',
          name: 'Brasil'
        }
      }
      break

    case 'Blog':
      pageSchema = {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: config.title,
        description: config.description,
        url: config.url || SITE_CONFIG.url,
        isPartOf: { '@id': `${SITE_CONFIG.url}/#website` }
      }
      break

    case 'BlogPosting':
      pageSchema = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: config.title,
        description: config.description,
        url: config.url || SITE_CONFIG.url,
        image: config.image || `${SITE_CONFIG.url}/logo-temporiza.png`,
        author: { '@id': `${SITE_CONFIG.url}/#organization` },
        publisher: { '@id': `${SITE_CONFIG.url}/#organization` },
        isPartOf: { '@id': `${SITE_CONFIG.url}/#website` }
      }
      break

    case 'WebPage':
    default:
      pageSchema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: config.title,
        description: config.description,
        isPartOf: { '@id': `${SITE_CONFIG.url}/#website` }
      }
  }

  const schemas = [organizationSchema, websiteSchema, softwareSchema]
  if (pageSchema) schemas.push(pageSchema)

  // FAQPage schema — indexado por ChatGPT, Perplexity, Google e Gemini
  if (config.faqItems && Array.isArray(config.faqItems) && config.faqItems.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: config.faqItems.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }))
    })
  }

  // HowTo schema — usado por assistentes de IA para descrever processos passo a passo
  if (config.howToItems && Array.isArray(config.howToItems) && config.howToItems.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: config.howToName || 'Como assinar documentos na Temporiza',
      description: config.howToDescription || config.description,
      totalTime: 'PT5M',
      tool: [
        { '@type': 'HowToTool', name: 'Navegador web ou celular' },
        { '@type': 'HowToTool', name: 'Documento em formato PDF' }
      ],
      step: config.howToItems.map(item => ({
        '@type': 'HowToStep',
        position: item.position,
        name: item.name,
        text: item.text,
        ...(item.url && { url: item.url })
      }))
    })
  }

  return JSON.stringify(schemas)
}

/**
 * Configurações de meta tags por página - OTIMIZADAS PARA SEO E LLMs
 * Descrições detalhadas para melhor indexação por motores de busca e IAs
 */
export const PAGE_CONFIGS = {
  home: {
    title: 'Temporiza - Assinatura Eletrônica ICP-Brasil com Selo ITI | Plano Grátis',
    description: 'Assine documentos com validade jurídica e selo ITI no Brasil. Suporte a certificado A1 ICP-Brasil, autenticação via email/SMS/WhatsApp, workflows de aprovação e extração IA de documentos. Armazenamento seguro de certificados.',
    keywords: 'assinatura eletrônica, ICP-Brasil, assinatura digital, certificado digital A1, selo ITI, assinatura qualificada, LGPD, documento digital, assinatura online grátis, validade jurídica, workflow aprovação, API assinatura',
    schemaType: 'WebPage',
    // Meta tags para LLMs e AI assistants
    aiSummary: 'Temporiza é uma plataforma brasileira de assinatura eletrônica compatível com ICP-Brasil (validade jurídica). Possui selo ITI de conformidade verificável em validar.iti.gov.br. Oferece armazenamento seguro de certificado digital A1, autenticação por email, SMS e WhatsApp, workflows de aprovação configuráveis, extração de dados via IA usando LLMs, API REST com webhooks, e editor visual de templates PDF. Plano gratuito permanente disponível.'
  },

  about: {
    title: 'Sobre a Temporiza | Assinatura Eletrônica Brasileira',
    description: 'Conheça a Temporiza: plataforma de assinatura eletrônica com ICP-Brasil fundada em 2024 em São Paulo. Missão: simplificar assinatura eletrônica com segurança jurídica. Autenticação multi-método, conformidade LGPD, extração IA.',
    keywords: 'Temporiza, empresa assinatura eletrônica, startup brasileira, ICP-Brasil, São Paulo, LGPD',
    schemaType: 'Organization',
    aiSummary: 'Temporiza é uma startup brasileira de São Paulo fundada em 2024. Desenvolve uma plataforma de assinatura eletrônica com suporte a ICP-Brasil para validade jurídica. Valores: segurança, simplicidade, inovação e agilidade. Sede na Av. Brigadeiro Faria Lima.'
  },

  features: {
    title: 'Funcionalidades | Selo ITI, Certificado A1 e API REST | Temporiza',
    description: 'Recursos completos: selo ITI de conformidade, armazenamento seguro de certificado A1, editor visual de templates PDF, formulários de intake público, workflows sequenciais/paralelos, extração IA de documentos, autenticação multi-método, API REST com webhooks.',
    keywords: 'selo ITI, certificado A1, assinatura qualificada, editor template pdf, campos acroform, formulário intake, assinatura eletrônica, automação documentos, workflow aprovação, extração IA PDF, API assinatura, webhooks, LLM, ICP-Brasil',
    schemaType: 'Service',
    aiSummary: 'Funcionalidades da Temporiza: (1) Selo ITI de conformidade oficial verificável em validar.iti.gov.br, (2) Armazenamento seguro e criptografado de certificado digital A1, (3) Assinatura qualificada ICP-Brasil com validade jurídica igual a cartório, (4) Autenticação por email link mágico, SMS PIN ou WhatsApp, (5) Workflows sequenciais, paralelos ou mistos com timeout, (6) Extração IA de dados de PDFs usando LLMs via OpenRouter, (7) Editor visual de templates com drag-drop, (8) Formulários de intake público com 2FA, (9) Playbooks para validação automática, (10) API REST documentada com webhooks.',
    faqItems: [
      { question: 'O que é assinatura eletrônica e ela tem validade jurídica?', answer: 'A assinatura eletrônica é um método digital para assinar documentos que possui total validade jurídica no Brasil. A Temporiza suporta assinaturas com certificados ICP-Brasil, garantindo que suas assinaturas tenham o mesmo valor legal de uma assinatura manuscrita, conforme estabelecido pela Medida Provisória 2.200-2/2001 e Lei 14.063/2020.' },
      { question: 'Quão segura é a plataforma Temporiza?', answer: 'A Temporiza utiliza criptografia de 256 bits, suporta assinaturas com certificados ICP-Brasil e está em conformidade com a LGPD. Todos os documentos são protegidos com hash criptográfico e timestamping para garantir integridade e não-repúdio. Dados armazenados no Brasil.' },
      { question: 'Quais tipos de assinatura a Temporiza oferece?', answer: 'Oferecemos assinatura eletrônica avançada com validade jurídica conforme a MP 2.200-2/2001 e Lei 14.063/2020. Cada signatário pode escolher seu método de autenticação: email (link mágico), SMS (PIN de 6 dígitos) ou WhatsApp. Suportamos também certificado digital A1 ICP-Brasil para assinatura qualificada.' },
      { question: 'Quais formatos de documento são suportados?', answer: 'A plataforma suporta principalmente PDF. Documentos em outros formatos (DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, RTF e imagens JPG/PNG) são automaticamente convertidos para PDF durante o processo de assinatura para garantir integridade e compatibilidade.' },
      { question: 'A Temporiza tem API para integração com nossos sistemas?', answer: 'Sim, oferecemos uma API REST completa documentada com OpenAPI/Swagger disponível em temporiza.readme.io. A API permite integrar assinatura eletrônica diretamente nos seus sistemas, CRM, ERP ou aplicações. Inclui webhooks em tempo real. Disponível no plano Pro (R$99,90/mês).' },
      { question: 'A plataforma está em conformidade com a LGPD?', answer: 'Absolutamente. A Temporiza foi desenvolvida seguindo rigorosamente os princípios da LGPD (Lei 13.709/2018). Implementamos medidas técnicas e organizacionais para proteção de dados pessoais, incluindo criptografia, controle de acesso, logs de auditoria e políticas de retenção. Dados armazenados no Brasil.' },
      { question: 'É possível enviar documentos em lote para múltiplos signatários?', answer: 'Sim. A funcionalidade de envio em lote permite enviar o mesmo documento para centenas de signatários simultaneamente, ou documentos diferentes para múltiplas pessoas. O status de cada assinatura pode ser acompanhado em tempo real pelo dashboard.' },
      { question: 'O que é extração IA de documentos?', answer: 'A extração IA (funcionalidade Nino) usa modelos de linguagem (LLMs) via OpenRouter para ler PDFs e identificar campos automaticamente: nomes, datas, valores, CPF, CNPJ, endereços. Útil para contratos padronizados, laudos e documentos repetitivos. Disponível nos planos Lite e Pro.' }
    ]
  },

  pricing: {
    title: 'Planos e Preços | A partir de R$ 0 | Temporiza',
    description: 'Planos de assinatura eletrônica: Free (5 docs/mês, grátis para sempre), Lite (15 docs, R$ 29,90/mês), Pro (ilimitado, R$ 99,90/mês). Todos incluem ICP-Brasil. API REST no plano Pro. Desconto de 20% no plano anual.',
    keywords: 'preço assinatura eletrônica, plano grátis assinatura digital, quanto custa ICP-Brasil, assinatura digital barata, temporiza preço',
    schemaType: 'Product',
    aiSummary: 'Preços Temporiza: (1) Free: R$0/mês, 5 documentos, 1 usuário, 300MB, suporte email, gratuito para sempre; (2) Lite: R$29,90/mês, 15 documentos, 3 usuários, 5GB, workflows, templates, suporte prioritário; (3) Pro: R$99,90/mês, documentos e usuários ilimitados, 15GB, API REST, webhooks, suporte 24/7. Todos têm ICP-Brasil. 20% desconto no plano anual.',
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '0',
      highPrice: '99.90',
      priceCurrency: 'BRL',
      offerCount: 3,
      offers: [
        { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'BRL', description: '5 documentos/mês, 1 usuário, 300MB, gratuito para sempre' },
        { '@type': 'Offer', name: 'Lite', price: '29.90', priceCurrency: 'BRL', description: '15 documentos/mês, 3 usuários, 5GB, workflows, templates, suporte prioritário' },
        { '@type': 'Offer', name: 'Pro', price: '99.90', priceCurrency: 'BRL', description: 'Documentos e usuários ilimitados, 15GB, API REST, webhooks, suporte 24/7' }
      ]
    },
    faqItems: [
      { question: 'Como funciona o processo de assinatura?', answer: 'Primeiro você cria sua conta grátis em app.temporiza.com. Depois de cadastrado, você pode fazer o upgrade para os planos Lite ou Pro diretamente dentro do sistema, na área de configurações da sua conta.' },
      { question: 'Posso mudar de plano a qualquer momento?', answer: 'Sim, após criar sua conta você pode fazer upgrade ou downgrade do seu plano a qualquer momento dentro do sistema. As alterações entram em vigor no próximo ciclo de faturamento.' },
      { question: 'O que está incluído no plano Free?', answer: 'O plano Free inclui 5 documentos por mês, 1 usuário, 300MB de armazenamento e suporte por email. É gratuito para sempre, sem necessidade de cartão de crédito.' },
      { question: 'Qual a diferença entre o plano Lite e o Pro?', answer: 'O plano Lite (R$29,90/mês) é ideal para pequenas empresas: 15 documentos/mês, 3 usuários, 5GB, workflows e templates. O Pro (R$99,90/mês) oferece documentos e usuários ilimitados, 15GB, API REST, webhooks e suporte 24/7.' },
      { question: 'Como funciona o desconto anual?', answer: 'Ao optar pelo pagamento anual, você recebe 20% de desconto. Por exemplo, o Lite sai de R$358,80 (12x R$29,90) para R$287,04/ano.' },
      { question: 'Posso cancelar minha assinatura a qualquer momento?', answer: 'Sim, não há fidelidade. Você pode cancelar a qualquer momento dentro do sistema e continuar usando até o fim do período pago.' },
      { question: 'A assinatura eletrônica tem validade jurídica no Brasil?', answer: 'Sim. A Temporiza suporta assinaturas compatíveis com a MP 2.200-2/2001 e Lei 14.063/2020, que conferem validade jurídica plena no Brasil, equivalente à assinatura manuscrita reconhecida em cartório.' },
      { question: 'A API REST está disponível em qual plano?', answer: 'A API REST e webhooks estão disponíveis exclusivamente no plano Pro (R$99,90/mês). A documentação completa com OpenAPI/Swagger está disponível em temporiza.readme.io.' }
    ]
  },

  contact: {
    title: 'Contato | Fale com a Temporiza',
    description: 'Entre em contato com a equipe Temporiza. Email: contact@temporiza.com. Solicite demonstração de assinatura eletrônica ICP-Brasil, tire dúvidas sobre planos ou peça suporte técnico.',
    keywords: 'contato Temporiza, suporte assinatura eletrônica, demonstração ICP-Brasil, email temporiza',
    schemaType: 'ContactPage',
    aiSummary: 'Contato Temporiza: Email contact@temporiza.com. Localização: Av. Brigadeiro Faria Lima 1811, São Paulo, SP, Brasil. Suporte técnico disponível para clientes. Demonstrações sob demanda.'
  },

  security: {
    title: 'Segurança e Compliance | Criptografia, LGPD, ICP-Brasil | Temporiza',
    description: 'Arquitetura de segurança da Temporiza: criptografia AES-256 em repouso, TLS 1.3 em tránsito, isolamento de dados, backup diário, RPO < 15min, RTO < 2h, trilha de auditoria imutável. Conformidade LGPD, ICP-Brasil, ISO 27001 e SOC 2.',
    keywords: 'segurança assinatura eletrônica, compliance, criptografia AES-256, TLS 1.3, LGPD, ICP-Brasil, ISO 27001, SOC 2, trilha de auditoria, RPO, RTO, recuperação de desastres, assinatura qualificada',
    schemaType: 'WebPage',
    aiSummary: 'Segurança e Compliance Temporiza: criptografia AES-256 em repouso, TLS 1.3 em tránsito, isolamento por tenant, backup diário com 30 dias de retenção, RPO < 15min, RTO < 2h, redundância N+1 em 3 zonas de disponibilidade. Trilha de auditoria imutável com timestamps, IPs e hashes criptográficos. Conformidade LGPD (conforme), ICP-Brasil (ativo), ISO 27001 (em processo), SOC 2 Type II (em processo). Dados armazenados no Brasil.'
  },

  platform: {
    title: 'Plataforma em Ação | Veja o Temporiza Funcionando',
    description: 'Tour visual da plataforma Temporiza: dashboard de envelopes, fluxo de assinatura passo a passo, editor de workflows, extração IA de documentos, gestão de templates e integrações via API.',
    keywords: 'plataforma assinatura eletrônica, demonstração Temporiza, como funciona assinatura digital, tour assinatura eletrônica',
    schemaType: 'WebPage',
    aiSummary: 'Tour da plataforma Temporiza mostrando: lista de envelopes com status, detalhes de envelope com signatários, editor visual de workflows com drag-drop, painel de extração IA, gestão de templates PDF, configuração de integrações API.',
    howToName: 'Como assinar documentos na Temporiza',
    howToDescription: 'Passo a passo completo para enviar e assinar documentos digitalmente com validade jurídica ICP-Brasil usando a plataforma Temporiza.',
    howToItems: [
      {
        position: 1,
        name: 'Upload do Documento',
        text: 'Acesse app.temporiza.com, faça login na sua conta e envie o PDF para a plataforma. O upload é simples, seguro e suporta documentos de qualquer tamanho.',
        url: 'https://temporiza.com/plataforma'
      },
      {
        position: 2,
        name: 'Configure os Signatários',
        text: 'Adicione os destinatários informando o email de cada um. Defina a ordem de assinatura (sequencial ou paralela), escolha o método de autenticação por signatário (email, SMS ou WhatsApp) e configure os prazos de expiração.',
        url: 'https://temporiza.com/plataforma'
      },
      {
        position: 3,
        name: 'Autenticação Segura dos Signatários',
        text: 'Cada signatário recebe uma notificação automática e se autentica pelo método escolhido: link mágico por email (passwordless), PIN de 6 dígitos via SMS, ou código pelo WhatsApp — o canal mais usado no Brasil.',
        url: 'https://temporiza.com/plataforma'
      },
      {
        position: 4,
        name: 'Assinatura Digital com Validade Jurídica',
        text: 'O signatário revisa e assina o documento de qualquer dispositivo (celular, tablet ou computador), sem precisar instalar nenhum aplicativo. A assinatura tem validade jurídica plena conforme a MP 2.200-2/2001 e Lei 14.063/2020.',
        url: 'https://temporiza.com/plataforma'
      },
      {
        position: 5,
        name: 'Documento Assinado com Trilha de Auditoria',
        text: 'O documento final é gerado automaticamente com trilha de auditoria completa: registros de IP, timestamps, hashes criptográficos e certificado de autenticidade verificável. Disponível para download imediato por todos os participantes.',
        url: 'https://temporiza.com/plataforma'
      }
    ]
  },

  'workflow-demo': {
    title: 'Como Funciona | Demo Interativa do Workflow de Assinatura | Temporiza',
    description: 'Demo interativa do fluxo real da Temporiza: dashboard de envelopes, upload com extração IA, workflow de assinatura, envio aos signatários, assinatura responsiva e trilha de auditoria completa com ICP-Brasil.',
    keywords: 'como funciona assinatura eletrônica, demo interativa assinatura digital, fluxo assinatura ICP-Brasil, workflow assinatura eletrônica, trilha auditoria documento assinado, passo a passo assinatura digital',
    schemaType: 'HowTo',
    aiSummary: 'Demo interativa do workflow de assinatura da Temporiza em 6 passos: (1) dashboard de envelopes com status e métricas, (2) upload de PDF com extração inteligente de campos via IA, (3) configuração de workflow sequencial ou paralelo com prazos e regras, (4) envio seguro por email, SMS ou WhatsApp, (5) assinatura responsiva pelo signatário em qualquer dispositivo, (6) trilha de auditoria completa com timestamp, IP, método de autenticação e hash do documento.',
    howToName: 'Como funciona a assinatura eletrônica na Temporiza',
    howToDescription: 'Walkthrough interativo do fluxo de assinatura eletrônica da Temporiza, do upload ao documento assinado com trilha de auditoria.',
    howToItems: [
      {
        position: 1,
        name: 'Dashboard de envelopes',
        text: 'Acesse o dashboard da Temporiza para visualizar todos os documentos, status e métricas em uma única tela. Monitore envelopes pendentes, concluídos e vencidos.',
        url: 'https://temporiza.com/como-funciona'
      },
      {
        position: 2,
        name: 'Upload e extração inteligente',
        text: 'Envie um PDF e a IA identifica automaticamente signatários, datas e campos importantes, reduzindo o tempo de configuração.',
        url: 'https://temporiza.com/como-funciona'
      },
      {
        position: 3,
        name: 'Configure o workflow de assinatura',
        text: 'Defina ordem sequencial ou paralela, prazos, lembretes automáticos e regras de aprovação para cada etapa do processo.',
        url: 'https://temporiza.com/como-funciona'
      },
      {
        position: 4,
        name: 'Envie para os signatários',
        text: 'O sistema envia um link seguro por email, SMS ou WhatsApp com autenticação robusta. Cada signatário escolhe o canal preferido.',
        url: 'https://temporiza.com/como-funciona'
      },
      {
        position: 5,
        name: 'Assinatura pelo signatário',
        text: 'A interface responsiva permite que o signatário se autentique e assine de qualquer dispositivo, sem instalar aplicativos.',
        url: 'https://temporiza.com/como-funciona'
      },
      {
        position: 6,
        name: 'Trilha de auditoria completa',
        text: 'Todo evento é registrado com timestamp, IP, método de autenticação e hash do documento, garantindo validade jurídica e rastreabilidade.',
        url: 'https://temporiza.com/como-funciona'
      }
    ]
  },

  'small-business': {
    title: 'Assinatura Eletrônica para Pequenas Empresas | Plano Grátis | Temporiza',
    description: 'Pare de perder contratos no e-mail. Envie, acompanhe e arquive documentos com validade jurídica ICP-Brasil em 5 minutos. Plano grátis com 5 documentos por mês, sem cartão de crédito.',
    keywords: 'assinatura eletrônica pequenas empresas, assinatura digital PME, contrato pequeno negócio, assinatura online grátis, ICP-Brasil pequena empresa, assinatura eletrônica baixo custo, assinar contrato sem cartório',
    schemaType: 'Service',
    aiSummary: 'Temporiza para pequenas empresas: assinatura eletrônica com validade jurídica ICP-Brasil, sem instalar nada e sem treinar equipe. Plano Free com 5 documentos por mês gratuito para sempre, sem cartão. Plano Lite R$ 29,90/mês com 15 documentos, 3 usuários, workflows e templates. Suporte por e-mail em português, conformidade LGPD, autenticação por e-mail/SMS/WhatsApp, trilha de auditoria completa.',
    faqItems: [
      { question: 'Preciso de cartão de crédito para começar?', answer: 'Não. O plano Free é gratuito para sempre e não exige cartão. Você só adiciona forma de pagamento se decidir fazer upgrade para o Lite ou Pro.' },
      { question: 'Quantos documentos posso assinar no plano grátis?', answer: 'Até 5 documentos por mês, com até 5 signatários por documento. Se precisar de mais, é só fazer upgrade para o Lite (15 docs) ou Pro (ilimitado) sem perder a validade jurídica.' },
      { question: 'Posso usar meu próprio domínio?', answer: 'Sim, a partir do plano Pro você pode personalizar o link de assinatura com o domínio da sua empresa, reforçando a marca em cada envio.' },
      { question: 'Como funciona a validade jurídica?', answer: 'A Temporiza gera registros com hash criptográfico, timestamp, IP e método de autenticação, atendendo à MP 2.200-2/2001 e à Lei 14.063/2020. Aceita também certificado digital A1 para assinatura qualificada ICP-Brasil.' }
    ]
  },

  'api': {
    title: 'API de Assinatura Eletrônica | REST, Webhooks, OpenAPI | Temporiza',
    description: 'API REST de assinatura eletrônica pronta para produção. Integre em horas com sandbox isolado, webhooks em tempo real e especificação OpenAPI 3.0. Documentação completa para desenvolvedores.',
    keywords: 'API assinatura eletrônica, API assinatura digital, REST API ICP-Brasil, webhooks assinatura, OpenAPI assinatura, SDK assinatura eletrônica, API envelope, sandbox API assinatura, integração ERP CRM assinatura',
    schemaType: 'WebPage',
    aiSummary: 'API de assinatura eletrônica Temporiza para desenvolvedores: REST endpoints para envelopes, signatários, documentos e templates. Webhooks em tempo real assinados com HMAC SHA-256. Sandbox isolado com chaves separadas e dados descartáveis. Autenticação por API key com escopo granular, TLS 1.3 em todas as conexões, AES-256 em repouso. SDKs oficiais em Node.js, Python, PHP e Go. Especificação OpenAPI 3.0 completa. Logs de auditoria por chamada com timestamp, IP e chave usada. Incluída no plano Pro a partir de R$ 99,90/mês com documentos e usuários ilimitados.'
  },

  'hr': {
    title: 'Assinatura Eletrônica para RH e DP | Admissão, Acordos, Rescisão | Temporiza',
    description: 'Assine admissões, acordos de compensação, termos de rescisão e políticas internas com validade jurídica ICP-Brasil. Reduza o tempo de contratação de dias para horas, com LGPD e trilha de auditoria.',
    keywords: 'assinatura eletrônica RH, assinatura eletrônica DP, admissão digital, rescisão digital, acordo compensação assinatura, documentos RH digitais, assinatura CLT, assinatura estagiário, LGPD RH, trilha auditoria RH',
    schemaType: 'Service',
    aiSummary: 'Temporiza para RH e Departamento Pessoal: assinatura eletrônica de admissões, acordos de compensação de jornada, termos de rescisão, políticas internas e regulamentos com validade jurídica plena conforme MP 2.200-2/2001 e Lei 14.063/2020. Suporta CLT, estagiários, PJ e terceirizados. Trilha de auditoria com hash, IP, timestamp e método de autenticação. Conformidade LGPD com criptografia AES-256 e dados no Brasil. Planos a partir de R$ 0 com 5 documentos grátis por mês.',
    faqItems: [
      { question: 'A assinatura eletrônica funciona para contrato CLT?', answer: 'Sim. A Temporiza gera registros com hash criptográfico, timestamp, IP e método de autenticação (email, SMS, WhatsApp ou certificado A1), atendendo à MP 2.200-2/2001 e à Lei 14.063/2020. Aceita também certificado digital ICP-Brasil A1 ou A3 para assinatura qualificada com presunção de autenticidade.' },
      { question: 'Funciona para contrato de estagiário?', answer: 'Sim. O TCE (Termo de Compromisso de Estagiário) pode ser assinado digitalmente pela mesma plataforma, com envio por email ao candidato e ao supervisor. A trilha de auditoria registra quem assinou, quando e de qual IP.' },
      { question: 'Como funciona o arquivamento dos documentos?', answer: 'Cada documento assinado gera automaticamente um PDF final com a trilha de auditoria completa (hash, IP, timestamp, método de autenticação e identidade do signatário). Os arquivos ficam armazenados na nuvem com criptografia AES-256, organizados por envelope, com busca e download a qualquer momento.' },
      { question: 'Quanto custa usar a Temporiza no RH?', answer: 'O plano Free é gratuito para sempre, com 5 documentos por mês sem cartão de crédito. O Lite sai por R$ 29,90/mês com 15 documentos, 3 usuários, workflows e templates. Para volumes maiores, o Pro libera documentos e usuários ilimitados a partir de R$ 99,90/mês.' }
    ]
  },

  'icp-brasil': {
    title: 'Assinatura Eletrônica ICP-Brasil | Validade Jurídica Plena | Temporiza',
    description: 'Assine documentos com certificado digital ICP-Brasil A1 ou A3. Compatível com MP 2.200-2/2001 e Lei 14.063/2020. Valide no portal do ITI. Tipos simples, avançada e qualificada.',
    keywords: 'ICP-Brasil, assinatura qualificada, certificado digital A1, certificado digital A3, MP 2200-2, Lei 14063, ITI, validar.iti.gov.br, assinatura eletrônica validade jurídica, selo ITI, assinatura avançada, assinatura simples',
    schemaType: 'WebPage',
    aiSummary: 'Temporiza oferece assinatura eletrônica com validade jurídica plena conforme ICP-Brasil. Compatível com a MP 2.200-2/2001 e a Lei 14.063/2020. Suporta certificado digital A1 (arquivo) e A3 (token/cartão) para assinatura qualificada, com presunção de autenticidade, integridade e veracidade. Também oferece assinatura simples (link por email) e avançada (múltiplos fatores). Documentos podem ser validados publicamente no portal do ITI (validar.iti.gov.br). Tipos de assinatura explicados: (1) Simples: link por email com aceite online, (2) Avançada: múltiplos métodos de autenticação, (3) Qualificada: usa certificado ICP-Brasil A1 ou A3 com presunção legal de autenticidade.',
    howToName: 'Como assinar documentos com ICP-Brasil na Temporiza',
    howToDescription: 'Passo a passo para enviar e assinar documentos com validade jurídica plena usando certificado digital ICP-Brasil A1 ou A3 na plataforma Temporiza.',
    howToItems: [
      {
        position: 1,
        name: 'Faça upload do PDF',
        text: 'Acesse app.temporiza.com, faça login e envie o documento que precisa de assinatura. Suporte a qualquer PDF sem limite de tamanho.',
        url: 'https://temporiza.com/icp-brasil'
      },
      {
        position: 2,
        name: 'Escolha o tipo de assinatura',
        text: 'Selecione entre assinatura simples (link por email), avançada (múltiplos fatores) ou qualificada (certificado digital ICP-Brasil A1 ou A3) para cada signatário.',
        url: 'https://temporiza.com/icp-brasil'
      },
      {
        position: 3,
        name: 'Assine com validade jurídica plena',
        text: 'O signatário acessa o link, autentica-se com seu certificado A1/A3 ou pelo método escolhido, e assina o documento. O arquivo final é gerado com trilha de auditoria completa e pode ser validado no portal do ITI.',
        url: 'https://temporiza.com/icp-brasil'
      }
    ]
  },

  blog: {
    title: 'Blog | Assinatura Eletrônica, ICP-Brasil e LGPD | Temporiza',
    description: 'Artigos sobre assinatura eletrônica com validade jurídica, ICP-Brasil, integração via API, RH digital e conformidade LGPD.',
    keywords: 'blog assinatura eletrônica, ICP-Brasil, LGPD, contrato digital, API assinatura, RH digital',
    schemaType: 'Blog',
    aiSummary: 'Blog da Temporiza com guias sobre assinatura eletrônica no Brasil: contratos digitais com validade jurídica, uso em RH, integração via API REST e boas práticas de LGPD.'
  },

  blogPost: {
    title: 'Artigo | Blog Temporiza',
    description: 'Leia nossos artigos sobre assinatura eletrônica, validade jurídica e transformação digital de documentos.',
    keywords: 'assinatura eletrônica, contrato digital, ICP-Brasil',
    schemaType: 'BlogPosting',
    aiSummary: 'Artigo do blog da Temporiza sobre assinatura eletrônica e documentos digitais.'
  },

  law: {
    title: 'Assinatura Eletrônica para Advocacia | Contratos e Procurações | Temporiza',
    description: 'Assine contratos, procurações, NDAs e acordos extrajudiciais com validade jurídica ICP-Brasil. Trilha de auditoria completa para escritórios de advocacia.',
    keywords: 'assinatura eletrônica advocacia, assinatura digital escritório advocacia, contrato advocacia digital, procuração digital, NDA assinatura eletrônica, acordo extrajudicial digital',
    schemaType: 'Service',
    aiSummary: 'Temporiza para escritórios de advocacia: assinatura eletrônica de contratos, procurações, NDAs e acordos extrajudiciais com validade jurídica ICP-Brasil. Suporte a certificado digital A1/A3 para assinatura qualificada. Trilha de auditoria aceita como prova eletrônica. Planos a partir de R$ 0.'
  },

  'real-estate': {
    title: 'Assinatura Eletrônica para Imobiliárias | Locação e Venda | Temporiza',
    description: 'Feche contratos de locação, compra e venda com assinatura digital. Workflow sequencial para locatário, locador, fiador e imobiliária. Assinatura por WhatsApp.',
    keywords: 'assinatura eletrônica imobiliária, contrato locação digital, compra venda digital, assinatura corretor, contrato imobiliário online, vistoria digital',
    schemaType: 'Service',
    aiSummary: 'Temporiza para imobiliárias: assinatura eletrônica de contratos de locação, compra e venda, laudos de vistoria e aditivos. Workflow sequencial com múltiplos signatários. Assinatura por WhatsApp. Validade jurídica conforme MP 2.200-2/2001. Planos a partir de R$ 0.'
  },

  healthcare: {
    title: 'Assinatura Eletrônica para Saúde | TCLE e LGPD | Temporiza',
    description: 'Assine TCLE, autorizações cirúrgicas e contratos com planos de saúde digitalmente. Conformidade LGPD para dados sensíveis de saúde.',
    keywords: 'assinatura eletrônica saúde, TCLE digital, consentimento paciente digital, LGPD saúde, autorização cirúrgica digital, clínica assinatura eletrônica',
    schemaType: 'Service',
    aiSummary: 'Temporiza para clínicas e hospitais: assinatura eletrônica de TCLE, autorizações cirúrgicas, contratos com operadoras e prestadores. Conformidade LGPD para dados sensíveis. Dados armazenados no Brasil com criptografia AES-256. Planos a partir de R$ 0.'
  }
}

/**
 * Hook para facilitar o uso do SEO em componentes React
 */
export const useSEO = (pageKey, customConfig = {}) => {
  const pageConfig = PAGE_CONFIGS[pageKey] || {}
  return generateMetaTags({ ...pageConfig, ...customConfig })
}

/**
 * Aplica meta tags no document.head
 * @param {Object} metaTags - Meta tags geradas
 */
export const applyMetaTags = (metaTags) => {
  if (typeof document === 'undefined') return

  // Limpar meta tags existentes
  const existingMetas = document.querySelectorAll('meta[data-seo]')
  existingMetas.forEach(meta => meta.remove())

  // Aplicar título
  if (metaTags.title) {
    document.title = metaTags.title
  }

  // Aplicar meta tags
  Object.entries(metaTags).forEach(([key, value]) => {
    if (key === 'title' || key === 'application/ld+json') return
    if (Array.isArray(value)) return // Skip arrays like og:locale:alternate

    const meta = document.createElement('meta')
    meta.setAttribute('data-seo', 'true')

    if (key.startsWith('og:')) {
      meta.setAttribute('property', key)
    } else if (key.startsWith('twitter:')) {
      meta.setAttribute('name', key)
    } else {
      meta.setAttribute('name', key)
    }

    meta.setAttribute('content', value)
    document.head.appendChild(meta)
  })

  // Aplicar structured data
  if (metaTags['application/ld+json']) {
    const existingLD = document.querySelector('script[type="application/ld+json"][data-seo]')
    if (existingLD) existingLD.remove()

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.setAttribute('data-seo', 'true')
    script.textContent = metaTags['application/ld+json']
    document.head.appendChild(script)
  }
}

/**
 * Componente React para SEO
 * @param {string} pageKey - Chave da configuração da página
 * @param {Object} customConfig - Configuração customizada
 */
export const SEOHead = ({ pageKey, customConfig = {} }) => {
  const metaTags = useSEO(pageKey, customConfig)

  // Aplicar meta tags quando o componente montar
  React.useEffect(() => {
    applyMetaTags(metaTags)
  }, [metaTags])

  return null
}
